import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { FeatureGroupsConfigGrid } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/FeatureGroupsConfigGrid'
import { Feature_Group_Config_Bool_Exp, Query_Root } from 'src/@types/graphql'
import { queryFeatureGroupConfigs } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/featureGroupsConfigQueries'
import { useUserLocale } from 'src/user/UserContext'
import { Autocomplete, Box, Button, Grid2, Stack, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import { Option } from 'src/shared/form/control'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getConfigStatusOptions,
  getOptionsForFilter,
  getTranslationForFeatureType,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import {
  fetchAllFeatureTypes,
  mapAllFeatureTypesToOptions,
} from 'src/screens/administration/configuration-management/features-configuration/features/FeaturesConfigGridUtils'
import { deleteFeatureGroupConfig } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/FeatureGroupsConfigGridUtils'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'

const FEATURE_GROUP_TABLE_NAME = 'FEATURE_GROUP'

type FeatureGroupsFilters = SpecificConfigFilter<'FEATURE_GROUP'>

const defaultFeatureGroupsTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
}

export const FeaturesConfigurationFeatureGroupsPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  // filter options
  const [featureGroupsTypeOptions, setFeatureGroupsTypeOptions] = useState<Option[]>(getOptionsForFilter([]))

  const featureGroupsStatusOptions: Option[] = useMemo(
    () => getOptionsForFilter(getConfigStatusOptions(getMessage)),
    [getMessage],
  )

  const defaultFeatureGroupsFiltersValues: FeatureGroupsFilters = useMemo(
    () => ({
      featureGroupsTypeFilter: featureGroupsTypeOptions[0],
      featureGroupsStatusFilter: featureGroupsStatusOptions[0],
    }),
    [featureGroupsTypeOptions, featureGroupsStatusOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    FEATURE_GROUP_TABLE_NAME,
    defaultFeatureGroupsTableSettings,
  )
  const [featureGroupsFilters, setFeatureGroupsFilters] = useFilterSettingsFromLocalStorage(
    defaultFeatureGroupsFiltersValues,
  )

  // delete modal state
  const [deleteModalLoading, setDeleteModalLoading] = useState(false)
  const [featureGroupToBeDeleted, setFeatureGroupToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const featureGroupsCondition: Feature_Group_Config_Bool_Exp = { _and: [] }
    if (featureGroupsFilters.featureGroupsTypeFilter.value != null) {
      const featureTypeConfigId = featureGroupsFilters.featureGroupsTypeFilter.value as number
      featureGroupsCondition._and?.push({ feature_type_id: { _eq: featureTypeConfigId } })
    }
    if (featureGroupsFilters.featureGroupsStatusFilter.value != null) {
      const isActive = featureGroupsFilters.featureGroupsStatusFilter.value === CONFIGURATION_STATUS.ACTIVE
      featureGroupsCondition._and?.push({ active: { _eq: isActive } })
    }

    const { data, error } = await urqlClient
      .query<
        {
          feature_group_config: Query_Root['feature_group_config']
          feature_group_config_aggregate: Query_Root['feature_group_config_aggregate']
        },
        {
          featureGroupsCondition: Feature_Group_Config_Bool_Exp
          offset: number
          limit: number
        }
      >(queryFeatureGroupConfigs, {
        featureGroupsCondition,
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.feature_group_config || !data.feature_group_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.feature_group_config.map((fgc) => ({
      id: fgc.id,
      name: fgc.feature_group_name_locale,
      feature_type: fgc.feature_type_config.names[locale],
      status: fgc.active,
      is_deletable: !!fgc.feature_group_deletability?.is_deletable,
    }))
    const fetchedRowsCount = data.feature_group_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings.paginationModel, featureGroupsFilters, urqlClient, locale])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevDataSettings) => ({
          ...prevDataSettings,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const onFeatureGroupsTypeChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setFeatureGroupsFilters({
        ...featureGroupsFilters,
        featureGroupsTypeFilter: newValue ?? defaultFeatureGroupsFiltersValues.featureGroupsTypeFilter,
      })
    },
    [
      tableSettings,
      featureGroupsFilters,
      defaultFeatureGroupsFiltersValues.featureGroupsTypeFilter,
      setTableSettings,
      setFeatureGroupsFilters,
    ],
  )

  const onFeatureGroupsStatusChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setFeatureGroupsFilters({
        ...featureGroupsFilters,
        featureGroupsStatusFilter: newValue ?? defaultFeatureGroupsFiltersValues.featureGroupsStatusFilter,
      })
    },
    [
      tableSettings,
      featureGroupsFilters,
      defaultFeatureGroupsFiltersValues.featureGroupsStatusFilter,
      setTableSettings,
      setFeatureGroupsFilters,
    ],
  )

  const handleResetFilters = useCallback(() => {
    setFeatureGroupsFilters(defaultFeatureGroupsFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultFeatureGroupsFiltersValues, tableSettings, setFeatureGroupsFilters, setTableSettings])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setFeatureGroupToBeDeleted(null)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!featureGroupToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteFeatureGroupConfig(urqlClient)(featureGroupToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        initRows()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteModalLoading(false)
        setFeatureGroupToBeDeleted(null)
      })
  }, [featureGroupToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    fetchAllFeatureTypes(urqlClient, locale)()
      .then(({ fetchedFeatureTypes }) => {
        const mappedOptionsOfFeatureTypes = getOptionsForFilter(
          mapAllFeatureTypesToOptions(fetchedFeatureTypes, locale),
        )

        setFeatureGroupsTypeOptions(mappedOptionsOfFeatureTypes)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [getMessage, locale, notificationService, urqlClient])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout
        title={`${getMessage('label.configuration.features.feature.groups')} ${getMessage('label.configuration')}`}
      >
        <PageLayout>
          <Stack spacing={5}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="body1" color="text.secondary" fontWeight="bold">
                  {getMessage('label.grid.filter')}
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <Autocomplete
                      options={featureGroupsTypeOptions}
                      value={featureGroupsFilters.featureGroupsTypeFilter}
                      onChange={(_, newVal) => onFeatureGroupsTypeChange(newVal)}
                      getOptionLabel={(opt) =>
                        getTranslationForFeatureType(getMessage, opt.value, featureGroupsTypeOptions)
                      }
                      renderInput={(params) => <TextField {...params} label={getMessage('label.feature.type')} />}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <Autocomplete
                      options={featureGroupsStatusOptions}
                      value={featureGroupsFilters.featureGroupsStatusFilter}
                      onChange={(_, newVal) => onFeatureGroupsStatusChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForStatus(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.status')} />}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container>
                  <Grid2 size={4}>
                    <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleResetFilters}>
                      {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                    </Button>
                  </Grid2>
                </Grid2>
              </Stack>
            </Box>
            <FeatureGroupsConfigGrid
              dataSettings={gridDataSettings}
              tableSettings={tableSettings}
              setTableSettings={setTableSettings}
              setFeatureGroupToBeDeleted={setFeatureGroupToBeDeleted}
            />
          </Stack>
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={!!featureGroupToBeDeleted}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.features.feature.group.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
